export function arrCopyAdd<T>(array: T[], value: T): T[] {
    const copy = [...array];
    copy.push(value);
    return copy;
}

export function arrCopyRemoveIndex<T>(array: T[], index: number): T[] {
    const copy = [...array];
    copy.splice(index, 1);
    return copy;
}

export function arrayUnique<T, K extends string | number>(array: T[], getKey: (element: T) => K): T[] {
    const seen = new Set<K>();
    return array.filter(item => {
        const key = getKey(item);
        if (seen.has(key)) {
            return false;
        } else {
            seen.add(key);
            return true;
        }
    });
}

export function sum(arr: number[]) {
    return arr.reduce((s, i) => s + i, 0);
}

/**
 * Round a monetary amount to `decimals` places, dropping IEEE-754 noise
 * (e.g. the ÷1000-to-thousands division that turns 637012946.59 into
 * 637012.9465900001) while preserving the real value. Applied both when loading
 * amounts and when parsing form input, so the stored value equals what the user
 * sees and what is sent to the backend. `null`/`NaN` → 0.
 */
export function roundAmount(v: number | null | undefined, decimals = 6): number {
    if (v == null || !isFinite(v)) return 0;
    return Number(v.toFixed(decimals));
}

export function cap(value: number, min: number, max?: number) {
    if (max === undefined) return Math.max(min, value);
    return Math.min(max, Math.max(min, value));
}

export function normalize_arr(arr: number[]) {
    const sum = arr.reduce((s, i) => s + i, 0);
    return arr.map(i => i / sum);
}

export function cum_sum(arr: number[]) {
    const c = [] as number[];
    arr.forEach((value, index) => {
        if (index === 0)
            c.push(value)
        else
            c.push(c[index - 1] + value);
    })
    return c;
}

export function deepCopy<T>(obj: T): T {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || "object" != typeof obj) return obj;

    // Handle Date
    if (obj instanceof Date) {
        copy = new Date();
        copy.setTime(obj.getTime());
        return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
        copy = [];
        for (let i = 0, len = obj.length; i < len; i++) {
            copy[i] = deepCopy(obj[i]);
        }
        return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
        copy = {};
        for (const attr in obj) {
            if (Object.hasOwn(obj, attr)) copy[attr] = deepCopy(obj[attr]);
        }
        return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
}


export function treeClone<T extends {children: T[]}>(tree: T) {
    const clone = Object.create(tree) as T
    if (clone.children) {
        clone.children = clone.children.map(c => treeClone(c))
    }
    return clone
}

/**
 * cyrb128 hash function
 * @param str string to hash
 * @param seed seed value
 * @returns hash string
 * @see https://stackoverflow.com/a/52171480/104380
 */
export function cyrb53(str: string, seed = 0): number {
    let h1 = 0xdeadbeef ^ seed,
        h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }

    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}


/**
 * Generate a random number btw min and max based on a random hash string
 */
export function randomHash(min: number, max: number, hash: string) {
    const hashInt = cyrb53(hash)
    const range = max - min
    return Math.floor(hashInt % range) + min
}

/**
 * Generate a random string
 */
export function randomId(length: number) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = chars.length;
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

/**
 * Get all unique objects in an array:
 * - objects with the same id are considered the same
 * - undefined objects are filtered out
 */
export function getUnique<T extends {
    id: number
}>(objects: (T | undefined)[]): T[] {
    const seen = new Set<number>();
    return objects.filter(obj => {
        if (obj === undefined) return false;
        if (seen.has(obj.id)) return false;
        seen.add(obj.id);
        return true;
    }) as T[];
}


/**
 * Encode to base64 (used in GCP storage client)
 * @param value 32-bit unsigned integer
 * @returns base64 encoded string
 */
export function encodeUint32ToBase64(value: number): string {
    // Ensure the value is within the range of a 32-bit unsigned integer
    if (value < 0 || value > 0xFFFFFFFF) {
        throw new RangeError('Value must be a 32-bit unsigned integer');
    }

    // Convert the integer to a byte array (big-endian)
    const bytes = new Uint8Array(4);
    bytes[0] = (value >> 24) & 0xFF;
    bytes[1] = (value >> 16) & 0xFF;
    bytes[2] = (value >> 8) & 0xFF;
    bytes[3] = value & 0xFF;

    // Convert byte array to binary string
    let binaryString = '';
    for (let i = 0; i < bytes.length; i++) {
        binaryString += String.fromCharCode(bytes[i]);
    }

    // Encode binary string to base64
    return btoa(binaryString);
}

/**
 * Decode to integer (used in GCP storage client)
 * @param input base64 encoded string
 * @returns 32-bit unsigned integer
 */
export function decodeBase64ToUint32(input: string): number {
    // Decode base64 input
    const binaryString = atob(input);

    // Convert binary string to a Uint8Array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    // Interpret bytes as a 32-bit unsigned integer (big-endian)
    return (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
}

export function lpad(s: string, pad: string, length: number) {
    return pad.repeat(length - s.length) + s;
}

/**
 * Shorten a string to a certain length. Also showing the start and end of the string.
 * Example: ABC...XYZ
 * @param s
 * @param totalLength Total length of the shortened string, including the dots
 */
export function shorten(s: string | null | undefined, totalLength: number = 9) {
    if (typeof s !== 'string') {
        s = String(s)
    }
    if (s.length <= totalLength) return s;
    const start = s.substring(0, (totalLength - 3) / 2);
    const end = s.substring(s.length - (totalLength - 3 - start.length));
    return start + '...' + end;
}

/**
 * Taken from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
 * @param type
 */
export function storageAvailable(type: 'localStorage' | 'sessionStorage') {
    try {
        const storage = window[type];
        const x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    } catch (e) {
        return false;
    }
}

type Primitive = string | number | boolean | undefined | null;
type AnyParamObj<T = Primitive> = Record<string, T | T[]>;

function appendValueToParams(p: URLSearchParams, key: string, value: Primitive): void {
    if (value === undefined || value === null) {
        return;
    }
    p.append(key, String(value));
}

function appendToParams(obj: AnyParamObj, p: URLSearchParams): void {
    Object.entries(obj).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach(v => appendValueToParams(p, key + '[]', v));
        } else {
            appendValueToParams(p, key, value)
        }
    });
}

export function toParams(obj: AnyParamObj | AnyParamObj[]): URLSearchParams {
    const p = new URLSearchParams();
    if (Array.isArray(obj)) {
        obj.forEach(o => {
            appendToParams(o, p);
        });
    } else {
        appendToParams(obj, p);
    }
    return p;
}

export function plural(str: string, count?: number | null) {
    return count === 1 ? str : str + 's';
}

export function plurals(count: number | null | undefined, single: string, plural: string): string {
    return count === 1 ? single : plural;
}

export function isNonEmptyArray(arg: any): arg is [any, ...any] {
    if (!arg) return false;
    if (Array.isArray(arg)) return arg.length >= 1;
    return false;
}

/**
 * Math.min and Math.max for large arrays.
 *
 * The maximum argument length for Math.min and Math.max is 65536.
 * @param data
 * @param defaultMin
 * @param defaultMax
 */
export function getMinMax(data: number[], defaultMin = Number.MAX_VALUE, defaultMax = Number.MIN_VALUE): [number, number] {
    data.forEach(d => {
        if (d < defaultMin) defaultMin = d;
        if (d > defaultMax) defaultMax = d;
    })
    return [defaultMin, defaultMax];
}

export function debug(val: any): string {
    return JSON.stringify(val, null, 2)
}

/**
 * Check if the ordering of `start` is the same as in `array`
 * @param array
 * @param start
 */
export function arrayOrderingCanStartWith<T>(array: T[], start: T[]): boolean {
    let nextI = 0;
    for (let i = 0; i < start.length; i++) {
        const index = array.indexOf(start[i]);
        if (index === -1) {
            // Okay, not found
        } else if (index !== nextI) {
            // Not the same order as in `start`
            return false;
        } else {
            // Okay, same order
            nextI = index + 1;
        }
    }
    return true;
}

/**
 * Check if the ordering of `start` is the same as in `array`
 * @param array
 * @param end
 */
export function arrayOrderingCanEndWith<T>(array: T[], end: T[]): boolean {
    return arrayOrderingCanStartWith(array.reverse(), end.reverse());
}

/**
 * Ensure that the elements are in the array and in the same order as in `elements`
 * @param array
 * @param elements
 */
export function ensureFirstElementsInArray<T>(array: T[] | null | undefined, elements: T[]): T[] {
    if (array === null || array === undefined) {
        array = [];
    }
    let nextI = 0;
    for (let i = 0; i < elements.length; i++) {
        const index = array.indexOf(elements[i]);
        if (index === -1) {
            // Not found, adding it at the start
            array.splice(nextI, 0, elements[i]);
        } else if (index !== nextI) {
            // Not the same order as in `start`
            array.splice(index, 1);
            array.splice(nextI, 0, elements[i]);
        } else {
            // Okay, same order
        }
        nextI++;
    }
    return array;
}

/**
 * Ensure that the elements are in the array and in the same order as in `elements`
 *
 * So array: [1, 2] and elements: [1, 4] will result in [2, 1, 4]
 * @param array
 * @param elements
 */
export function ensureLastElementsInArray<T>(array: T[] | null | undefined, elements: T[]): T[] {
    if (array === null || array === undefined) {
        array = [];
    }
    let insertPos = array.length;
    for (let i = elements.length - 1; i >= 0; i--) {
        const el = elements[i];
        const index = array.indexOf(el);
        if (index === -1) {
            // Not found, add at the end
            array.splice(insertPos, 0, el);
        } else if (index !== insertPos - 1) {
            // Found, but not at the correct position
            array.splice(index, 1);
            array.splice(insertPos - 1, 0, el);
        }
        insertPos--;
    }
    return array
}

export function getLocalStorageJson<T = any>(key: string): T | undefined {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : undefined;
    } catch {
        return undefined;
    }
}